import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import "./EmployeeView.css";
import { format } from "date-fns";

const EmployeeView = () => {
    const { id } = useParams(); // ✅ employeeUniqueId from URL
    const [employee, setEmployee] = useState(null);

    useEffect(() => {
        const fetchEmployeeDetails = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/personal-info/${id}`);
                if (!response.ok) throw new Error("Failed to fetch employee details");
                const data = await response.json();
                setEmployee(data);
            } catch (error) {
                console.error("Error fetching employee details:", error);
            }
        };

        if (id) fetchEmployeeDetails();
    }, [id]);

    if (!employee) return <div className="preview-container">Loading...</div>;

    const address = employee.addresses?.[0] || {};
    const educationDetails = employee.educationDetails || [];
    const certificates = employee.certificates || [];
    const experienceDetails = employee.experiences || [];
    const bankDetails = employee.bankDetails || [];
    const idDetails = employee.employeeIDDetails || {};
    return (
        <div className="preview-container">
            <form>
                <div className="preview-content">
                    <h2 className="preview-title">Employee Details</h2>

                    <table className="preview-table">
                        <thead className="preview-main-header">
                            <tr>
                                <th colSpan="4">Personal Details</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <th className="label-cell">First Name</th>
                                <td>{employee.givenName || "N/A"}</td>
                                <th className="label-cell">Last Name</th>
                                <td>{employee.surname || "N/A"}</td>
                            </tr>
                            <tr>
                                <th className="label-cell">Gender</th>
                                <td>{employee.gender?.gender || "N/A"}</td>
                                <th className="label-cell">Date of Birth</th>
                                <td>{format(employee.dateOfBirth,'dd-MM-yyyy') || "N/A"}</td>
                            </tr>
                            <tr>
                                <th className="label-cell">Mobile</th>
                                <td>{employee.contactNumber || "N/A"}</td>
                                <th className="label-cell">Email</th>
                                <td>{employee.email || "N/A"}</td>
                            </tr>
                            <tr>
                                <th className="label-cell">Marital Status</th>
                                <td>{employee.maritalStatus?.maritalStatus || "N/A"}</td>
                                <th className="label-cell">Spouse Contact Number</th>
                                <td>{employee.spouseContactNumber || "N/A"}</td>
                            </tr>
                            <tr>
                                <th className="label-cell">Father Given Name</th>
                                <td>{employee.fatherGivenName || "N/A"}</td>
                                <th className="label-cell">Mother Given Name</th>
                                <td>{employee.motherGivenName || "N/A"}</td>
                            </tr>
                            <tr>
                                <th className="label-cell">Nationality</th>
                                <td >{employee.nationality?.nationalityName || "N/A"}</td>
                                <th className="label-cell">Religion</th>
                                <td>{employee.religion?.religionName || "N/A"}</td>
                            </tr>
                            <tr>
                                <th className="label-cell">Reservation Category</th>
                                <td>{employee.reservationCategory?.categoryName || "N/A"}</td>
                                <th className="label-cell">Physically Handicapped</th>
                                <td>{employee.physicallyHandicapped ? "Yes" : "No"}</td>
                            </tr>
                        </tbody>
                    </table>

                    <table className="preview-table">
                        <thead className="preview-main-header">
                            <tr>
                                <th colSpan="2">Present Address</th>
                                <th colSpan="2">Permanent Address</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td className="label-cell">Address 1</td>
                                <td>{address.presentAddressLine1 || "N/A"}</td>
                                <td className="label-cell">Address 1</td>
                                <td>{address.permanentAddressLine1 || "N/A"}</td>
                            </tr>
                            <tr>
                                <td className="label-cell">Address 2</td>
                                <td>{address.presentAddressLine2 || "N/A"}</td>
                                <td className="label-cell">Address 2</td>
                                <td>{address.permanentAddressLine2 || "N/A"}</td>
                            </tr>
                            <tr>
                                <td className="label-cell">Country</td>
                                <td>{address.presentCountry || "N/A"}</td>
                                <td className="label-cell">Country</td>
                                <td>{address.permanentCountry || "N/A"}</td>
                            </tr>
                            <tr>
                                <td className="label-cell">State</td>
                                <td>{address.presentState || "N/A"}</td>
                                <td className="label-cell">State</td>
                                <td>{address.permanentState || "N/A"}</td>
                            </tr>
                            <tr>
                                <td className="label-cell">District</td>
                                <td>{address.presentDistrict || "N/A"}</td>
                                <td className="label-cell">District</td>
                                <td>{address.permanentDistrict || "N/A"}</td>
                            </tr>
                            <tr>
                                <td className="label-cell">Block</td>
                                <td>{address.presentBlock || "N/A"}</td>
                                <td className="label-cell">Block</td>
                                <td>{address.permanentBlock || "N/A"}</td>
                            </tr>
                            <tr>
                                <td className="label-cell">Pincode</td>
                                <td>{address.presentPostCode || "N/A"}</td>
                                <td className="label-cell">Pincode</td>
                                <td>{address.permanentPostCode || "N/A"}</td>
                            </tr>
                        </tbody>
                    </table>
                    {/* ===== EDUCATION ===== */}
                    <table className="preview-table">
                        <thead className="preview-main-header">
                            <tr>
                                <th colSpan="9">Education Details</th>
                            </tr>
                        </thead>
                        <thead className="preview-subheader">
                            <tr>
                                <th>Qualification</th>
                                <th>Degree</th>
                                <th>Specialization</th>
                                <th>Institution</th>
                                <th>Grade / Percentage</th>
                                <th>Year of Passing</th>
                                <th>Verified By</th>
                                <th>Certificate</th>
                            </tr>
                        </thead>
                        <tbody>
                            {employee.educations?.length ? (
                                employee.educations.map((edu, index) => (
                                    <tr key={index}>
                                        <td>{edu.qualification?.qualificationName || "-"}</td>
                                        <td>{edu.degreeType?.degreeType || "-"}</td>
                                        <td>{edu.specializationResponse?.specializationName|| edu.specialization}</td>
                                        <td>{edu.institutionName || "-"}</td>
                                        <td>{edu.gradeOrPercentage || "-"}</td>
                                        <td>{edu.yearOfPassing || "-"}</td>
                                        <td>{edu.verifiedBy || "-"}</td>
                                        <td>
                                            {edu.certificate ? (
                                                <a
                                                    href={
                                                        edu.certificate.startsWith("http")
                                                            ? edu.certificate
                                                            : `${process.env.REACT_APP_FILE_BASE_URL}/${edu.certificate}`
                                                    }
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    View
                                                </a>
                                            ) : (
                                                "-"
                                            )}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="8">No Education Records Found</td>
                                </tr>
                            )}
                        </tbody>
                    </table>


                    {/* ===== OTHER CERTIFICATES ===== */}
                    <table className="preview-table">
                        <thead className="preview-main-header">
                            <tr>
                                <th colSpan="5">Other Certificates</th>
                            </tr>
                        </thead>
                        <thead className="preview-subheader">
                            <tr>
                                <th>Certificate Name</th>
                                <th>Certificate Number</th>
                                <th>Issued Date</th>
                                <th>Validate Date</th>
                                <th>File</th>
                            </tr>
                        </thead>
                        <tbody>
                            {certificates?.length ? (
                                certificates.map((cert, index) => (
                                    <tr key={index}>
                                        <td>{cert.certificateName || "-"}</td>
                                        <td>{cert.certificateNumber || "-"}</td>
                                        <td>{cert.issuedDate ?format((cert.issuedDate), 'dd-MM-yyyy') : "-"}</td>
                                        <td>{cert.validateDate ? format((cert.validateDate), 'dd-MM-yyyy') : "-"}</td>
                                        <td>
                                            {cert.certificateFileName ? (
                                                <a
                                                    href={cert.certificateFileName.startsWith("http") ? cert.certificateFileName : `${process.env.REACT_APP_FILE_BASE_URL}/${cert.certificateFileName}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    View
                                                </a>
                                            ) : (
                                                "-"
                                            )}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5">No Certificates Found</td>
                                </tr>
                            )}
                        </tbody>
                    </table>


                    {/* ===== EXPERIENCE ===== */}
                    <table className="preview-table">
                        <thead className="preview-main-header">
                            <tr>
                                <th colSpan="8">Experience Details</th>
                            </tr>
                        </thead>
                        <thead className="preview-subheader">
                            <tr>
                                <th>Company</th>
                                <th>Designation</th>
                                <th>Skill Set</th>
                                <th>From</th>
                                <th>To</th>
                                <th>Salary</th>
                                <th>Appointment Letter</th>
                                <th>Relieving Letter</th>
                            </tr>
                        </thead>
                        <tbody>
                            {employee.experiences?.length ? (
                                employee.experiences.map((exp, index) => (
                                    <tr key={index}>
                                        <td>{exp.companyName || "-"}</td>
                                        <td>{exp.designationName || "-"}</td>
                                        <td>{exp.skillSet || "-"}</td>
                                        <td>
                                            {exp.fromDate
                                                ? format((exp.fromDate), 'dd-MM-yyyy')
                                                : "-"}
                                        </td>
                                        <td>
                                            {exp.toDate
                                                ? format((exp.toDate), 'dd-MM-yyyy')
                                                : "-"}
                                        </td>
                                        <td>{exp.salaryDrawn ? `₹${exp.salaryDrawn}` : "-"}</td>
                                        <td>
                                            {exp.appointmentLetter ? (
                                                <a
                                                    href={
                                                        exp.appointmentLetter.startsWith("http")
                                                            ? exp.appointmentLetter
                                                            : `${process.env.REACT_APP_FILE_BASE_URL}/${exp.appointmentLetter}`
                                                    }
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    View
                                                </a>
                                            ) : (
                                                "-"
                                            )}
                                        </td>
                                        <td>
                                            {exp.relievingLetter ? (
                                                <a
                                                    href={
                                                        exp.relievingLetter.startsWith("http")
                                                            ? exp.relievingLetter
                                                            : `${process.env.REACT_APP_FILE_BASE_URL}/${exp.relievingLetter}`
                                                    }
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    View
                                                </a>
                                            ) : (
                                                "-"
                                            )}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="8">No Experience Records Found</td>
                                </tr>
                            )}
                        </tbody>
                    </table>


                    {/* ===== BANK DETAILS ===== */}
                    <table className="preview-table">
                        <thead className="preview-main-header">
                            <tr>
                                <th colSpan="7">Bank Details</th>
                            </tr>
                        </thead>
                        <thead className="preview-subheader">
                            <tr>
                                <th>Account Holder</th>
                                <th>Account Number</th>
                                <th>Account Type</th>
                                <th>Bank Name</th>
                                <th>Branch Name</th>
                                <th>IFSC</th>
                                <th>Passbook</th>
                            </tr>
                        </thead>
                        <tbody>
                            {employee.bankDetails?.length ? (
                                employee.bankDetails.map((bank, i) => (
                                    <tr key={i}>
                                        <td>{bank.accountHolderName || "-"}</td>
                                        <td>{bank.accountNumber || "-"}</td>
                                        <td>{bank.accountType?.accountTypeName || "-"}</td>
                                        <td>{bank.bankName || "-"}</td>
                                        <td>{bank.branchName || "-"}</td>
                                        <td>{bank.ifscCode || "-"}</td>
                                        <td>
                                            {bank.passbookFileName ? (
                                                <a
                                                    href={
                                                        bank.passbookFileName.startsWith("http")
                                                            ? bank.passbookFileName
                                                            : `${process.env.REACT_APP_FILE_BASE_URL}/${bank.passbookFileName}`
                                                    }
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    View
                                                </a>
                                            ) : (
                                                "-"
                                            )}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="7">No Bank Details Found</td>
                                </tr>
                            )}
                        </tbody>
                    </table>


                    {/* ===== EMPLOYEE ID DETAILS ===== */}
                    <table className="preview-table">
                        <thead className="preview-main-header">
                            <tr>
                                <th colSpan="2">Employee ID Details</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <th>Aadhaar Number</th>
                                <td>{idDetails.aadhaarNumber || "-"}</td>
                            </tr>
                            <tr>
                                <th>Name as per Aadhaar</th>
                                <td>{idDetails.nameAsPerAadhaar || "-"}</td>
                            </tr>
                            <tr>
                                <th>Aadhaar Card</th>
                                <td>
                                    {idDetails.aadhaarFileName ? (
                                        <a
                                            href={
                                                idDetails.aadhaarFileName.startsWith("http")
                                                    ? idDetails.aadhaarFileName
                                                    : `${process.env.REACT_APP_FILE_BASE_URL}/${idDetails.aadhaarFileName}`
                                            }
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            View
                                        </a>
                                    ) : (
                                        "-"
                                    )}
                                </td>
                            </tr>
                            <tr>
                                <th>PAN Number</th>
                                <td>{idDetails.panCardNumber || "-"}</td>
                            </tr>
                            <tr>
                                <th>Name as per PAN</th>
                                <td>{idDetails.nameAsPerPanCard || "-"}</td>
                            </tr>
                            <tr>
                                <th>PAN Card</th>
                                <td>
                                    {idDetails.panCardFileName ? (
                                        <a
                                            href={
                                                idDetails.panCardFileName.startsWith("http")
                                                    ? idDetails.panCardFileName
                                                    : `${process.env.REACT_APP_FILE_BASE_URL}/${idDetails.panCardFileName}`
                                            }
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            View
                                        </a>
                                    ) : (
                                        "-"
                                    )}
                                </td>
                            </tr>

                            {/* Conditional UAN */}
                            {idDetails.hasUanNumber && (
                                <>
                                    <tr>
                                        <th>UAN Number</th>
                                        <td>{idDetails.uanNumber || "-"}</td>
                                    </tr>
                                    <tr>
                                        <th>UAN Member ID</th>
                                        <td>{idDetails.uanMemberId || "-"}</td>
                                    </tr>
                                    <tr>
                                        <th>UAN Card</th>
                                        <td>
                                            {idDetails.uanCardFileName ? (
                                                <a
                                                    href={
                                                        idDetails.uanCardFileName.startsWith("http")
                                                            ? idDetails.uanCardFileName
                                                            : `${process.env.REACT_APP_FILE_BASE_URL}/${idDetails.uanCardFileName}`
                                                    }
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    View
                                                </a>
                                            ) : (
                                                "-"
                                            )}
                                        </td>
                                    </tr>
                                </>
                            )}

                            {/* Conditional ESI */}
                            {idDetails.hasEsiNumber && (
                                <>
                                    <tr>
                                        <th>ESI Number</th>
                                        <td>{idDetails.esiNumber || "-"}</td>
                                    </tr>
                                    <tr>
                                        <th>ESI Card</th>
                                        <td>
                                            {idDetails.esiCardFileName ? (
                                                <a
                                                    href={
                                                        idDetails.esiCardFileName.startsWith("http")
                                                            ? idDetails.esiCardFileName
                                                            : `${process.env.REACT_APP_FILE_BASE_URL}/${idDetails.esiCardFileName}`
                                                    }
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    View
                                                </a>
                                            ) : (
                                                "-"
                                            )}
                                        </td>
                                    </tr>
                                </>
                            )}

                            <tr>
                                <th>Father Name</th>
                                <td>{`${idDetails.fatherGivenName || "-"} ${idDetails.fatherSurname || ""}`.trim()}</td>
                            </tr>
                            <tr>
                                <th>Mother Name</th>
                                <td>{`${idDetails.motherGivenName || "-"} ${idDetails.motherSurname || ""}`.trim()}</td>
                            </tr>
                            <tr>
                                <th>Spouse Name</th>
                                <td>{`${idDetails.spouseGivenName || "-"} ${idDetails.spouseSurname || ""}`.trim()}</td>
                            </tr>
                            <tr>
                                <th>Nominee Name</th>
                                <td>{idDetails.nomineeName || "-"}</td>
                            </tr>
                            <tr>
                                <th>Relationship with Nominee</th>
                                <td>{idDetails.nomineeRelationship?.relationshipName || "-"}</td>
                            </tr>
                            <tr>
                                <th>Employee Passport Photo</th>
                                <td>
                                    {idDetails.employeepassportSizeFileName ? (
                                        <a
                                            href={
                                                idDetails.employeepassportSizeFileName.startsWith("http")
                                                    ? idDetails.employeepassportSizeFileName
                                                    : `${process.env.REACT_APP_FILE_BASE_URL}/${idDetails.employeepassportSizeFileName}`
                                            }
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            View
                                        </a>
                                    ) : (
                                        "-"
                                    )}
                                </td>
                            </tr>
                        </tbody>
                    </table>

                </div>
            </form>
        </div>
    );
};

export default EmployeeView;
